import React, { useEffect, useMemo, useState } from 'react';
import { useSocket } from '../contexts/SocketContext';
import { useLiveMultiplier } from '../game/useLiveMultiplier';
import { canModerateRider, maskPlayerName, playerInitials, shownRiderChroma } from '../utils/displayName';
import { useHiddenRiders } from '../utils/hiddenRiders';
import { RiderActionsButton } from './RiderActions';
import { formatCompactMoney, formatMoney } from '../utils/money';
import { getRunTier } from '../utils/runTiers';
import { useRiderRoster } from '../game/riderRoster';
import { CHROMA_PALETTES } from './entities/SkierEntity';

const PALETTES: [string, string][] = [
  ['rgba(117,232,255,.12)', '#75e8ff'], ['rgba(156,255,87,.12)', '#9cff57'],
  ['rgba(255,202,98,.12)', '#ffca62'], ['rgba(192,132,252,.12)', '#c084fc'],
  ['rgba(251,146,60,.12)', '#fb923c'], ['rgba(248,113,113,.12)', '#f87171'],
];

function avatarColor(id: string): [string, string] {
  let hash = 0;
  for (let index = 0; index < id.length; index++) hash = (hash * 31 + id.charCodeAt(index)) | 0;
  return PALETTES[Math.abs(hash) % PALETTES.length];
}

const LiveBets: React.FC<{ showCurrentConditions?: boolean }> = ({ showCurrentConditions = true }) => {
  const { gameInfo, player } = useSocket();
  const currentMultiplier = useLiveMultiplier();
  const phase = gameInfo?.phase;
  const rawBets = useMemo(() => (gameInfo?.bets ?? []) as any[], [gameInfo?.bets]);
  const cashouts = useMemo(() => (gameInfo?.cashouts ?? []) as any[], [gameInfo?.cashouts]);
  const [displayBets, setDisplayBets] = useState<any[]>([]);
  // The slope's own ranking and skier colours (published by the canvas).
  const roster = useRiderRoster();
  // Re-render when a rider is hidden or shown again.
  useHiddenRiders();

  useEffect(() => {
    if (phase === 'STARTING') return setDisplayBets([]);
    if (phase === 'IN_PROGRESS' || phase === 'CRASHED') {
      const deduped = rawBets.reduce((rows: any[], bet: any) => {
        const index = rows.findIndex(row => row.playerId === bet.playerId);
        if (index >= 0) rows[index] = bet; else rows.push(bet);
        return rows;
      }, []).sort((a: any, b: any) => (b.amount ?? 0) - (a.amount ?? 0));
      setDisplayBets(deduped);
    }
  }, [phase, gameInfo?.bets, rawBets]);

  // Follow the slope ranking when the canvas has one; riders it does not know keep wager order.
  const rankedBets = useMemo(() => {
    if (roster.entries.length === 0) return displayBets;
    const rank = new Map(roster.entries.map((entry, index) => [entry.playerId, index]));
    return [...displayBets].sort((a, b) => (rank.get(a.playerId) ?? Infinity) - (rank.get(b.playerId) ?? Infinity));
  }, [displayBets, roster.entries]);
  const chromaOf = useMemo(() => new Map(roster.entries.map(entry => [entry.playerId, entry.chroma])), [roster.entries]);

  const summary = useMemo(() => displayBets.reduce((totals, bet) => {
    totals.totalBet += bet.amount;
    const cashout = cashouts.find(item => item.playerId === bet.playerId);
    if (cashout) totals.totalOut += cashout.profit + bet.amount;
    else if (phase === 'IN_PROGRESS') totals.stillIn++;
    return totals;
  }, { totalBet: 0, totalOut: 0, stillIn: 0 }), [displayBets, cashouts, phase]);

  const mono: React.CSSProperties = { fontFamily: "'JetBrains Mono', monospace" };
  const runTier = getRunTier(currentMultiplier);
  if (displayBets.length === 0) return (
    <div style={{ minHeight: 180, display: 'grid', placeItems: 'center', textAlign: 'center' }}>
      <div><div style={{ width: 34, height: 34, margin: '0 auto 10px', borderRadius: '50%', border: '1px solid var(--border-mid)', display: 'grid', placeItems: 'center', color: 'var(--ice)' }}>↗</div><p style={{ color: 'var(--text-secondary)', fontSize: '.78rem', margin: 0 }}>The mountain is quiet</p><p style={{ color: 'var(--text-muted)', fontSize: '.65rem', margin: '4px 0 0', ...mono }}>Wagers appear when the run begins.</p></div>
    </div>
  );

  return (
    <div className={`live-bets${displayBets.length <= 2 ? ' is-compact' : ''}`}>
      <div className="flex justify-between items-center mb-3" style={{ fontSize: '.65rem', color: 'var(--text-muted)', ...mono }}>
        <span>{displayBets.length} rider{displayBets.length !== 1 ? 's' : ''}{summary.stillIn > 0 && <span style={{ color: 'var(--gold)', marginLeft: 6 }}>{summary.stillIn} riding</span>}</span>
        <span>{formatCompactMoney(summary.totalBet)} staked{summary.totalOut > 0 && <span style={{ color: 'var(--green)', marginLeft: 6 }}>· {formatCompactMoney(summary.totalOut)} out</span>}</span>
      </div>
      <div className="flex items-center" style={{ gap: 8, paddingBottom: 7, borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', fontSize: '.57rem', letterSpacing: '.1em', textTransform: 'uppercase', ...mono }}>
        <span style={{ width: 28 }} /><span style={{ flex: 1 }}>Rider</span><span style={{ width: 52, textAlign: 'right' }}>Wager</span><span style={{ width: 74, textAlign: 'right' }}>Result</span>
      </div>
      <div style={{ maxHeight: 420, overflowY: 'auto' }}>
        {rankedBets.map((bet, index) => {
          const cashout = cashouts.find(item => item.playerId === bet.playerId);
          const isActive = !cashout && phase === 'IN_PROGRESS';
          const chroma = shownRiderChroma(bet.chroma ?? chromaOf.get(bet.playerId), bet.playerId);
          const palette = chroma !== undefined ? CHROMA_PALETTES[chroma] : undefined;
          const [background, color] = palette ? [palette.tagBg, palette.label] : avatarColor(bet.playerId);
          return (
            <div key={bet.playerId} data-rider-row={bet.playerId} className="flex items-center" style={{ gap: 8, minHeight: 44, borderBottom: index < displayBets.length - 1 ? '1px solid var(--border)' : 'none', fontSize: '.7rem' }}>
              <div data-rider-avatar style={{ width: 28, height: 28, flex: 'none', display: 'grid', placeItems: 'center', border: `1px solid ${color}44`, borderRadius: 8, background, color, fontSize: '.55rem', fontWeight: 700, ...mono }}>{playerInitials(bet.username, bet.playerId)}</div>
              <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                <span title={maskPlayerName(bet.username, bet.playerId)} style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-secondary)', ...mono }}>{player?.id === bet.playerId ? 'YOU' : maskPlayerName(bet.username, bet.playerId)}</span>
                {canModerateRider(bet.playerId, player?.id) && <RiderActionsButton playerId={bet.playerId} username={bet.username} />}
              </span>
              <span style={{ width: 52, textAlign: 'right', color: 'var(--text-secondary)', ...mono }}>{formatCompactMoney(bet.amount)}</span>
              <span style={{ width: 74, textAlign: 'right', ...mono }}>{cashout ? <><span style={{ color: 'var(--green)' }}>+{formatMoney(cashout.profit)}</span><small style={{ display: 'block', color: 'var(--text-muted)' }}>{cashout.multiplier.toFixed(2)}×</small></> : isActive ? <span style={{ color: 'var(--gold)' }}>{formatCompactMoney(bet.amount * currentMultiplier)}</span> : phase === 'CRASHED' ? <span style={{ color: 'var(--red)' }}>Lost</span> : <span style={{ color: 'var(--text-muted)' }}>Waiting</span>}</span>
            </div>
          );
        })}
      </div>
      {roster.queued > 0 && <p className="rider-queue-note">+{roster.queued} waiting for a slot</p>}
      {showCurrentConditions && displayBets.length <= 2 && phase === 'IN_PROGRESS' && <div className="sparse-run-card" style={{ '--tier-color': runTier.color } as React.CSSProperties}>
        <span>Current conditions</span>
        <div><strong>{runTier.name}</strong><b>{currentMultiplier.toFixed(2)}x</b></div>
        <small>{summary.stillIn > 0 ? `${summary.stillIn} rider${summary.stillIn === 1 ? '' : 's'} holding the line` : 'All riders have secured their exits'}</small>
      </div>}
    </div>
  );
};

export default LiveBets;
